import { Decimal } from 'decimal.js';

export interface QuantityPriceTier {
  minQuantity: number;
  maxQuantity: number | null;
  price: string;
}

export interface OrderItemDisplayData {
  /** 一覧描画用の一意キー(明細ID・商品バリエーションID) */
  key: number;
  productCode: string | null;
  modelNumber: string | null;
  name: string;
  manufacturer: string | null;
  spec: string | null;
  unitPrice: string;
  baseUnitPrice: string;
  quantityPrices: QuantityPriceTier[];
  /** フォーム入力中の数量(バラ総数)。荷姿換算表示に使う */
  quantity: number;
  snp: number;
  /** 荷姿名(荷姿マスタ参照)。未設定なら荷姿換算表示を出さない */
  packagingTypeName: string | null;
  /** バラの単位名。未設定なら荷姿換算表示のバラ数に単位を付けない */
  unitLabel: string | null;
  amount: string;
  taxClass: string | null;
  lastPurchasedAt: string | null;
  /** 明細ごとの希望納期 */
  desiredDeliveryDate: string | null;
  thumbnailUrl: string | null;
  /** 再注文のコピー元での単価。現在の単価と比べて表示する */
  previousUnitPrice: string | null;
}

export interface OrderTotalsData {
  subtotal: number;
  tax10Amount: number;
  tax8Amount: number;
  taxTotal: number;
  totalAmount: number;
}

/**
 * バックエンドの AmountCalculator.calculate_total_amounts と同等のロジック。
 * - 明細金額 = quantity × unit_price（切り捨て）
 * - 税区分別小計に対して税率を掛けて切り捨て
 */
export const calculateOrderTotals = (items: OrderItemDisplayData[]): OrderTotalsData => {
  let subtotal = new Decimal(0);
  let tax10Subtotal = new Decimal(0);
  let tax8Subtotal = new Decimal(0);

  for (const item of items) {
    const amount = new Decimal(item.amount || '0');
    subtotal = subtotal.add(amount);

    if (item.taxClass === 'TAX_RATE_10') {
      tax10Subtotal = tax10Subtotal.add(amount);
    } else if (item.taxClass === 'TAX_RATE_8_REDUCED') {
      tax8Subtotal = tax8Subtotal.add(amount);
    }
  }

  const tax10Amount = tax10Subtotal.mul('0.10').toDecimalPlaces(0, Decimal.ROUND_DOWN);
  const tax8Amount = tax8Subtotal.mul('0.08').toDecimalPlaces(0, Decimal.ROUND_DOWN);
  const taxTotal = tax10Amount.add(tax8Amount);
  const totalAmount = subtotal.add(taxTotal);

  return {
    subtotal: subtotal.toNumber(),
    tax10Amount: tax10Amount.toNumber(),
    tax8Amount: tax8Amount.toNumber(),
    taxTotal: taxTotal.toNumber(),
    totalAmount: totalAmount.toNumber(),
  };
};

/**
 * 数量に応じた実効単価を返す。
 * 数量別単価tierにマッチするものがあればその単価を、なければbase単価を返す。
 */
export const resolveUnitPrice = (
  baseUnitPrice: string,
  quantityPrices: QuantityPriceTier[],
  quantity: number,
): string => {
  if (quantityPrices.length === 0 || quantity <= 0) return baseUnitPrice;
  for (const tier of quantityPrices) {
    if (quantity >= tier.minQuantity && (tier.maxQuantity === null || quantity <= tier.maxQuantity)) {
      return tier.price;
    }
  }
  return baseUnitPrice;
};

/**
 * 明細1行の金額を計算する（quantity × unit_price、切り捨て）
 *
 * 数量はバラ総数で扱うため、入数(snp)は金額に乗じない。
 */
export const calculateItemAmount = (unitPrice: string, quantity: number): string => {
  return new Decimal(unitPrice).mul(quantity).toDecimalPlaces(0, Decimal.ROUND_DOWN).toFixed(0);
};
